import {
  AlertBannerPropsT,
  ConsentTypeT,
  MakePaymentResponseT,
  ModalConfigT,
  RegistrationAddressT,
  StateOptionT,
  stateValues,
} from './types';

// eslint-disable-next-line max-len
export const mcafeeTrustedSiteAltText = 'McAfee SECURE sites help keep you safe from identity theft, credit card fraud, spyware, spam, viruses and online scams';

export const noBalanceDueHeader = 'Your outstanding balance is $0.';
export const noBalanceDueMessage = "You don't have any payments due.";

export const noValidPaymentMethodsHeader = 'There is an issue with your payment method.';
export const noValidPaymentMethodsMessageHelper = (href: string) => (
  // The href, link text, and surrounding text are all covered in the NcMakeAPaymentNoPaymentMethodsError story.
  // Code cov treats this as a miss because the fragment is not explicitly checked 😂
  // istanbul ignore next -- @preserve
  <>
    <a href={href}>
      Manage Payment Methods
    </a> to update your debit card or bank account.
  </>);
export const fallbackErrorHeader = 'Something went wrong.';
export const fallbackErrorMessage = (
  <>Please call us at <a className="non-breaking" href="tel:8773922014">(877) 392-2014</a> for assistance.</>
);

// Payments
export const ACH_NEXT_DAY_CUTOFF = 18; // 6 PM Central Time
export const LOC_SAME_DAY_DEBIT_CUTOFF = 20; // 8 PM Central Time
export const CENTRAL_TIME_ZONE = 'America/Chicago';

export const initialModalConfig: ModalConfigT = {
  body: '',
  primaryText: '',
  secondaryText: '',
  show: false,
  title: '',
};

export const externalLinkModalConfig: ModalConfigT = {
  body: (
    <p>
      By clicking this link you will be leaving the NetCredit website — www.netcredit.com.
      You will be automatically directed to a website that is not directly controlled by NetCredit.
      By clicking OK, you acknowledge that you are leaving the NetCredit website.
    </p>
  ),
  primaryText: 'OK',
  secondaryText: 'Cancel',
  show: false,
  title: 'Leaving NetCredit.com',
};

export const originalPricingTextFirstHalf = 'The fee table below is the original pricing when you first set up your account '
  + 'and is not reflective of the Fee Saver reductions you may have earned. Please click';
export const originalPricingTextLastHalf = ' to see your current fee table as of today. '
  + 'Your lender may cancel or make changes to the Fee Saver program at any time.';

export const cashAdvanceFeeTooltipText = 'This fee can be reduced with consistent on-time or early payments.';

export const LOAN_STATUS = {
  CALLED_DUE: 'called_due',
  CURRENT: 'current',
  DISCHARGED: 'discharged',
  FUNDING_FAILED: 'funding_failed',
  PAST_DUE: 'past_due',
  PENDING_RESCISSION: 'pending_rescission',
  PENDING_TRANSFER: 'pending_transfer',
  RESCINDED: 'rescinded',
};

export const defaultSetValueOpts = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

export const defaultFetchErrorMessage = 'Something went wrong.';

export const MAKE_PAYMENT_RESPONSES_MAP: MakePaymentResponseT = {
  immediate_payment: 'complete',
  scheduled_payment: 'scheduled',
};

export const invalidDateErrorMessage = 'Date is invalid. Please try the next business day.';
export const supportNumberDisplay = '(877) 392-2014';

export const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

export const genericErrorMessage = 'Whoops, something went wrong. Please try again.';
export const notFoundErrorMessage = "Sorry, but the page you're looking for can't be found.";

// can occur when a customer is in LP
export const noOpenLoanErrorHeader = 'No open loan found for this account.';
export const noOpenLoanErrorMessage = 'You do not have an outstanding balance for payment.';

export const initialAlertBannerProps: AlertBannerPropsT = {
  dismissible: false,
  heading: '',
  message: '',
  show: false,
  variant: 'info',
};

export const somethingWentWrongAlertBannerProps: AlertBannerPropsT = {
  heading: 'Something went wrong.',
  message: 'Please try again.',
  show: true,
  variant: 'danger',
};

export const HEADER_LOGO_URI = '/assets/images/NC_color_TV_tagline.svg';

// eslint-disable-next-line max-len
export const incomeSourceDisclaimer = 'Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation.';

export const emailFormatErrorMessage = 'Enter your email in this format: name@example.com.';

export const phoneNumberValidationMessage = 'Enter your 10-digit phone number.';
export const employerPhoneNumberValidationMessage = "Enter your employer's 10-digit phone number.";
export const invalidPhoneNumberMessage = 'Enter a valid 10-digit phone number.';

export const MIN_PASSWORD_LENGTH = 8;
export const confirmMinLengthError = 'Re-type your password to continue.';
export const minLengthError = `Enter a password with a minimum of ${MIN_PASSWORD_LENGTH} characters.`;
export const minLengthHelperText = `Must include at least ${MIN_PASSWORD_LENGTH} characters.`;
export const passwordMatchError = 'Passwords do not match. Please try again.';

export const emptyAddress: RegistrationAddressT = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  state: '',
};

export const stateOptions: StateOptionT[] = stateValues.map((state) => ({
  label: state,
  value: state,
}));

export const A11Y_CLICK_KEYS_MAP: Record<string, string> = {
  ENTER: 'Enter',
  SPACE: ' ',
};

export const maxBorrowAmount = 10000;
export const minBorrowAmount = 1000;
export const amountTooHighValidationMessage = 'Amount is too high.';
export const amountTooLowValidationMessage = 'Amount is too low.';
export const amountValidationMessage = 'Enter an amount between $1,000 - $10,000.';

export type UnableToContinueReasonT = 'existingEmail' | 'unableToVerify';

export const unableToContinueHeadings: Record<UnableToContinueReasonT, string> = {
  existingEmail: 'Looks like you already have an account.',
  unableToVerify: "We couldn't verify your identity this way.",
};

export const consentsList: ConsentTypeT[] = [
  'consent_to_small_dollar_rule_ecommunication',
  'consent_to_electronic_communications',
  'consent_to_obtain_credit_reports',
  'consent_to_privacy_policy',
];
export const DELAWARE_CONSENT: ConsentTypeT = 'delaware_disclosure';

export const LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY = 'appData:loanAmount,loanPurpose';
export const STATE_LOCAL_STORAGE_KEY = 'appData:state';

export const SEND_CODE_COUNTDOWN_SECONDS = 60;
export const APPLY_UUID_COOKIE_KEY = 'apply_uuid';

export const expectedFundingDateTooltipContent = 'It may take your financial institution an additional 24 hours '
 + 'to process your funds.'
;
